"use client";

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import apiService from "../../../service/Api";
import OtpVerificationForm from "./OTPVerification";
import CreatePasswordForm from "./CreatePasswordForm";
import SuccessStep from "./RegistrationSuccessModal";
import { useDispatch } from "react-redux";
import { setReferralAuth } from "../../../store/slice/ReferralAuthSlice";
import { login } from "../../../store/slice/AuthSlice";

type Step = "validating" | "otp" | "createPassword" | "success" | "invalid";

interface CompleteProfileResponse {
  message: string;
  referral_partner: { id: number };
  access_token: string;
  refresh_token: string;
  user: {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    is_superuser: boolean;
    permissions: string[];
    referral_partner: number;
  };
}

function extractError(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (!data) return fallback;
  if (typeof data === "string") return data;
  if (typeof data === "object") {
    const detail = (data as { detail?: string }).detail;
    if (detail) return detail;
    return Object.entries(data as Record<string, unknown>)
      .map(([k, v]) =>
        Array.isArray(v) ? `${k}: ${v.join(", ")}` : `${k}: ${String(v)}`,
      )
      .join(" | ");
  }
  return fallback;
}

export default function CompleteRegistration() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const token = searchParams.get("token") ?? "";

  const [step, setStep] = useState<Step>("validating");
  const [email, setEmail] = useState("");
  const [partnerId, setPartnerId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Resend invite state
  const [resendEmail, setResendEmail] = useState("");
  const [resendSubmitting, setResendSubmitting] = useState(false);
  const [resendDone, setResendDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setErrorMessage("No invite token was found in this link.");
      setStep("invalid");
      return;
    }

    (async () => {
      try {
        // GET request — backend validates token AND sends OTP in one call
        const res = await apiService.get<{ email: string; message: string }>(
          `/api/refferal-program/validate-invite/?token=${token}`,
        );
        setEmail(res.data.email);
        setStep("otp");
        toast.info("A verification code has been sent to your email.");
      } catch (err) {
        const message = extractError(err, "");
        const expired = message.toLowerCase().includes("expired");
        setIsExpired(expired);
        setErrorMessage(
          expired
            ? "Your invite link has expired."
            : "This invite link is invalid or has already been used. Please contact your administrator.",
        );
        setStep("invalid");
      }
    })();
  }, [token]);

  const handleVerify = async (otp: string) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await apiService.post(
        "/api/refferal-program/verify-code/",
        { email, code: otp },
        true,
      );
      toast.success("Email verified!");
      setStep("createPassword");
    } catch (err) {
      toast.error(extractError(err, "Invalid code. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  // Resend OTP by re-hitting validate-invite (it re-sends OTP automatically)
  const handleResend = async () => {
    try {
      await apiService.get(
        `/api/refferal-program/validate-invite/?token=${token}`,
      );
      toast.info("A new code has been sent to " + email);
    } catch (err) {
      toast.error(extractError(err, "Failed to resend code."));
    }
  };

  const handleCreatePassword = async (
    password: string,
    confirmPassword: string,
  ) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const response = await apiService.post<CompleteProfileResponse>(
        "/api/refferal-program/complete-profile/",
        {
          email,
          password,
          confirm_password: confirmPassword,
        },
        true,
      );

      if (response.data?.referral_partner?.id) {
        dispatch(
          setReferralAuth({
            user: {
              ...response.data.user,
              referral_partner: response.data.referral_partner.id,
            },
            access_token: response.data.access_token,
            refresh_token: response.data.refresh_token,
          }),
        );
        dispatch(
          login({
            user: {
              username: response.data.user.username,
              first_name: response.data.user.first_name,
              last_name: response.data.user.last_name,
              is_supper_user: "",
              permissions: response.data.user.permissions,
            },
            access_token: response.data.access_token,
            refresh_token: response.data.refresh_token,
            is_2fa_enabled: false,
          }),
        );
        toast.success("Setup complete! Welcome to the partner program.");
        setPartnerId(String(response.data.referral_partner.id));
        setStep("success");
      }
    } catch (err) {
      toast.error(extractError(err, "Something went wrong. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendInvite = async () => {
    if (!resendEmail.trim() || resendSubmitting) return;
    setResendSubmitting(true);
    try {
      await apiService.post(
        "/api/refferal-program/resend-invite/",
        { email: resendEmail.trim() },
        false,
      );
      setResendDone(true);
    } catch (err) {
      toast.error(extractError(err, "Failed to resend invite."));
    } finally {
      setResendSubmitting(false);
    }
  };

  const handleDashboardClick = () => {
    if (partnerId) navigate(`/referral-dashboard/${partnerId}`);
  };

  // ── Validating ────────────────────────────────────────────────────────────

  if (step === "validating") {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">
          Validating your invite link…
        </Typography>
      </Box>
    );
  }

  // ── Invalid / Expired ─────────────────────────────────────────────────────

  if (step === "invalid") {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          p: 3,
          textAlign: "center",
        }}
      >
        <Typography fontSize={48}>{isExpired ? "⏰" : "🔗"}</Typography>

        <Typography fontWeight={700} fontSize={20}>
          {isExpired ? "Your invite link has expired" : "Invalid invite link"}
        </Typography>

        <Typography color="text.secondary" maxWidth={400} fontSize={14}>
          {errorMessage}
        </Typography>

        {/* Expired — let them request a new link */}
        {isExpired && !resendDone && (
          <Box
            sx={{
              mt: 1,
              width: "100%",
              maxWidth: 380,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <Typography fontSize={13} color="text.secondary">
              Enter your email address to receive a new invite link.
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="Your email address"
              type="email"
              value={resendEmail}
              onChange={(e) => setResendEmail(e.target.value)}
            />
            <Button
              variant="contained"
              fullWidth
              disabled={resendSubmitting || !resendEmail.trim()}
              onClick={handleResendInvite}
              sx={{ textTransform: "none", borderRadius: 2 }}
              startIcon={
                resendSubmitting ? (
                  <CircularProgress size={14} color="inherit" />
                ) : null
              }
            >
              {resendSubmitting ? "Sending…" : "Send new invite link"}
            </Button>
          </Box>
        )}

        {/* Resend confirmation */}
        {isExpired && resendDone && (
          <Box
            sx={{
              mt: 1,
              p: 2,
              bgcolor: "success.light",
              borderRadius: 2,
              maxWidth: 380,
              width: "100%",
            }}
          >
            <Typography fontSize={14} color="success.dark" fontWeight={500}>
              ✅ If a pending account exists for that email, a new invite has
              been sent. Please check your inbox.
            </Typography>
          </Box>
        )}

        {/* Truly invalid — no resend option, contact admin */}
        {!isExpired && (
          <Button
            variant="outlined"
            sx={{ mt: 1, textTransform: "none", borderRadius: 2 }}
            onClick={() => navigate("/")}
          >
            Go to homepage
          </Button>
        )}
      </Box>
    );
  }

  // ── Active steps ──────────────────────────────────────────────────────────

  return (
    <>
      <OtpVerificationForm
        open={step === "otp"}
        onClose={() => {}}
        phone={email}
        onVerify={handleVerify}
        onResend={handleResend}
        isSubmitting={submitting}
        onBack={() => {}}
      />
      <CreatePasswordForm
        open={step === "createPassword"}
        onClose={() => {}}
        email={email}
        onSubmit={handleCreatePassword}
        isSubmitting={submitting}
        onBack={() => setStep("otp")}
      />
      <SuccessStep
        open={step === "success"}
        onDashboardClick={handleDashboardClick}
      />
    </>
  );
}
